// src/components/ImportNotes.tsx
import React, { useMemo, useState } from "react";
import Page from "./Page";
import PageTitle from "./PageTitle";
import "./components.css";
import { Info, X } from "lucide-react";
import { CueNote } from "../types";
import { setNotes as setNotesInStore } from "../state/notes-store";
import { usePackets } from "../hooks/usePackets";
import StarterPackCard from "./import/StarterPackCard";
import SavedKitsList from "./import/SavedKitsList";
import AdvancedImportPanel from "./import/AdvancedImportPanel";

// Modal + starter pack helpers (LIFTED back to page root)
import RoleSelectModal from "./RoleSelectModal";
import { STARTER_NOTES } from "../notes.starter";
import { toCueNotes } from "../lib/typesafe";
import { createPacket, setCurrentPacket } from "../persistence/packets";

export default function ImportNotes() {
  const {
    packets,
    currentPacketId,
    currentPacketName,
    createBlank,
    rename,
    remove,
    selectAsCurrent,
    loadAndGoTag,
    exportDocx,
  } = usePackets();

  // DOCX example modal
  const [docxExampleOpen, setDocxExampleOpen] = useState<boolean>(false);

  // Role Select modal — lifted here to avoid stacking context/z-index issues
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const openStarterRoleSelect = () => setRoleModalOpen(true);
  const closeStarterRoleSelect = () => setRoleModalOpen(false);

  function processAndGo(arr: CueNote[]) {
    const normalized = arr.map((n) => ({
      ...n,
      title: (n.title ?? "").trim(),
      triggers: (n.triggers ?? []).filter(Boolean),
    }));
    setNotesInStore(normalized);
    window.location.href = "/setup/tag";
  }

  const infoLine = useMemo(() => {
    if (!currentPacketName) return "No Prep Kit selected";
    return `“${currentPacketName}”`;
  }, [currentPacketName]);

  // ---------- Starter pack creation (page-level, matches original behavior) ----------
  async function createGeneralStarterPacket() {
    try {
      const defaultName = `General Starter Pack — ${new Date().toLocaleDateString()}`;
      const name = prompt("Name your new Prep Kit", defaultName);
      if (!name) {
        // fallback: drop notes directly (legacy-friendly path)
        processAndGo(STARTER_NOTES as CueNote[]);
        return;
      }
      const p = await createPacket(name, (STARTER_NOTES as CueNote[]).map(n => ({
        ...n,
        title: (n.title ?? "").trim(),
        triggers: (n.triggers ?? []).filter(Boolean),
      })));
      await setCurrentPacket(p.id);
      await selectAsCurrent(p.id);
      window.location.href = "/setup/tag";
    } finally {
      closeStarterRoleSelect();
    }
  }

  async function createRoleStarterPacket(opts: { path: string; defaultPrefix: string }) {
    try {
      const res = await fetch(opts.path, { cache: "no-store" });
      if (!res.ok) throw new Error(`Failed to load pack ${opts.path} (${res.status})`);
      const json = await res.json();
      const arr = toCueNotes(json);
      if (!arr) throw new Error("Invalid starter pack format");
      const notes = (arr as CueNote[]).map(n => ({
        ...n,
        title: (n.title ?? "").trim(),
        triggers: (n.triggers ?? []).filter(Boolean),
      }));

      const defaultName = `${opts.defaultPrefix} — ${new Date().toLocaleDateString()}`;
      const name = prompt("Name your new Prep Kit", defaultName) || defaultName;

      const p = await createPacket(name, notes);
      await setCurrentPacket(p.id);
      await selectAsCurrent(p.id);
      window.location.href = "/setup/tag";
    } catch (e: any) {
      alert(e?.message || `Failed to create ${opts.defaultPrefix}`);
    } finally {
      closeStarterRoleSelect();
    }
  }

  const createCSMStarterPacket = () =>
    createRoleStarterPacket({ path: "/starter-packs/csm.json", defaultPrefix: "CSM Starter Pack" });
  const createAEStarterPacket = () =>
    createRoleStarterPacket({ path: "/starter-packs/ae.json", defaultPrefix: "AE Starter Pack" });
  const createPMStarterPacket = () =>
    createRoleStarterPacket({ path: "/starter-packs/pm.json", defaultPrefix: "PM Starter Pack" });
  const createSWEStarterPacket = () =>
    createRoleStarterPacket({ path: "/starter-packs/swe.json", defaultPrefix: "SWE Starter Pack" });
  const createMKTStarterPacket = () =>
    createRoleStarterPacket({ path: "/starter-packs/marketing.json", defaultPrefix: "Marketing Starter Pack" });
  // -----------------------------------------------------------------------

  return (
    <Page>
      <PageTitle>Home</PageTitle>

      {/* Context banner */}
      <div className="vstack-sm" style={{ marginTop: -6, marginBottom: 8 }}>
        <div className="small muted flex items-center gap-2">
          <Info size={18} aria-hidden="true" />
          <span>
            <strong>Currently editing:</strong>&nbsp;
            <strong>{infoLine}</strong>.&nbsp; Imports will be added to this kit.
          </span>
        </div>
        <p className="muted" style={{ margin: 0 }}>
          Create a <strong>Prep Kit</strong> from a <strong>Role Pack</strong> + <strong>General Starter Pack</strong>{" "}
          (recommended), or import your notes (.docx). We’ll keep your writing intact.
        </p>
      </div>

      {/* Starter/More Options card (now calls up to open modal) */}
      <StarterPackCard
        onOpenRoleSelect={openStarterRoleSelect}
        onNavigateTag={() => (window.location.href = "/setup/tag")}
        onCreateBlank={createBlank}
        setCurrentAndRefresh={selectAsCurrent}
        showDocxExample={() => setDocxExampleOpen(true)}
      />

      {/* Saved Kits */}
      <SavedKitsList
        packets={packets}
        currentPacketId={currentPacketId}
        onSelectAsCurrent={selectAsCurrent}
        onLoad={loadAndGoTag}
        onRename={async (id, curName) => {
          const name = prompt("Rename Prep Kit", curName);
          if (!name || name === curName) return;
          await rename(id, name);
        }}
        onDelete={async (id) => {
          if (!confirm("Delete this Prep Kit? This cannot be undone.")) return;
          await remove(id);
        }}
        onExport={exportDocx}
      />

      {/* Friendly Paste / JSON */}
      <AdvancedImportPanel onProcessNotes={processAndGo} />

      {/* Role selection modal — at page root to avoid flashing/stacking issues */}
      <RoleSelectModal
        open={roleModalOpen}
        onClose={closeStarterRoleSelect}
        onSelectGeneral={createGeneralStarterPacket}
        onSelectCSM={createCSMStarterPacket}
        onSelectAE={createAEStarterPacket}
        onSelectPM={createPMStarterPacket}
        onSelectSWE={createSWEStarterPacket}
        onSelectMKT={createMKTStarterPacket}
      />

      {/* Tiny DOCX example modal (unchanged) */}
      {docxExampleOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="DOCX structure example">
          <div className="modal">
            <div className="modal-header">
              <div style={{ fontWeight: 700 }}>DOCX structure example</div>
              <button className="button-ghost" onClick={() => setDocxExampleOpen(false)} aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              <div className="muted small" style={{ marginBottom: 8 }}>
                Use this simple structure (case-insensitive, blank lines allowed):
              </div>
              <pre
                style={{
                  background: "#111827",
                  color: "#e5e7eb",
                  padding: 12,
                  borderRadius: 8,
                  overflowX: "auto",
                  marginBottom: 8,
                }}
              >{`CueBoard 1: Elevator Pitch

Notes:
- I have 10 years of experience in customer success
- I've built and scaled high performing teams
- I love solving problems for customers
`}</pre>
            </div>
            <div className="modal-actions">
              <button className="button button-secondary" onClick={() => setDocxExampleOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
